import { gql } from '@apollo/client'
import type { BuildGraphqlQueryFragment } from '@ztechapps/common.utils'
import { buildGraphqlQuery } from '@ztechapps/common.utils'

export const remarksActivityFragment = `remarksActivity {
  _id
  activity
  by {
    _id
    profile {
      _id
      name
      picture
    }
  }
  updatedAt
  isDeleted
}`

export const billOfMaterialsFragment = `billOfMaterials {
   _id
   product {
      _id
      code
      name
      coverImage
      coverImageThumb
      status
      inventory {
        _id
        quantityOnHand
      }
      hasSubComponents
      operations {
        _id
        total
      }
      totalManufactureCost
      miscellaneousCost
      baseUom {
        _id
        code
        symbol
      }
      unitOfMeasurePrecision
   }
   quantity
   remarks
   requiredToBuild
 }`

export const assetsFragment = `assets(includeAllStatuses: true) {
  _id
  title
  subtitle
  type
  status
  url
  largeUrl
  mediumUrl
  thumbUrl
}`

export const relatedProductFragment = `relatedProducts {
  _id
  code
  name
}`

export const relatedPostsFragment = `relatedPosts {
      _id
      title
      summary
      cover
    }`

export const productVendorsFragment = `vendors {
  _id
  vendorCode
  price
  vendor {
    _id
    company {
      _id
      name
    }
    profile {
      _id
      name
    }
    status
  }
  remarks
  url
}`

export const operationsFragment = `operations {
    _id
    name
    cost {
      _id
      laborCost
      estimatedDuration
      perUnitCost
    }
    remarks
    total
    statusActions {
      _id
      status
      actionHookId
    }
 }`

export const remarksActivityFragment1 = gql`
  fragment productRemarksActivityFragment on Activity {
    _id
    activity
    by {
      _id
      profile {
        _id
        name
        picture
      }
    }
    updatedAt
    isDeleted
  }
`

export const billOfMaterialsFragment1 = gql`
  fragment billOfMaterialsFragment on ProductBillOfMaterial {
    _id
    product {
      _id
      code
      name
      coverImage
      coverImageThumb
      status
      requiredComputedQuantityOnHand
      computedQuantityOnHand
      inventory {
        _id
        quantityOnHand
      }
      hasSubComponents
      operations {
        _id
        total
      }
      totalManufactureCost
      miscellaneousCost
      baseUom {
        _id
        code
        symbol
      }
      unitOfMeasurePrecision
      workOrderUom {
        _id
        code
        symbol
      }
      workOrderConversionFactor
    }
    quantity
    remarks
    requiredToBuild
  }
`

export const assetsFragment1 = gql`
  fragment assetsFragment on Asset {
    _id
    url
    mediumUrl
    largeUrl
    thumbUrl
    title
    subtitle
    type
    status
  }
`

export const relatedProductFragment1 = gql`
  fragment relatedProductFragment on Product {
    _id
    code
    name
    coverImage
    coverImageThumb
    status
  }
`

export const relatedPostsFragment1 = gql`
  fragment relatedPostsFragment on Blog {
    _id
    title
    summary
    cover
  }
`

export const productVendorsFragment1 = gql`
  fragment productVendorsFragment on ProductVendor {
    _id
    vendorCode
    price
    vendor {
      _id
      company {
        _id
        name
      }
      profile {
        _id
        name
      }
      status
    }
    remarks
    url
  }
`

export const operationsFragment1 = gql`
  fragment operationsFragment on ProductOperation {
    _id
    name
    cost {
      _id
      laborCost
      estimatedDuration
      perUnitCost
    }
    remarks
    total
    statusActions {
      _id
      status
      actionHookId
    }
  }
`

export const uomReferenceFragment = gql`
  fragment uomReferenceFragment on UnitOfMeasure {
    _id
    code
    name
    symbol
    category
    conversionFactor
  }
`

export const defaultProductFragment = gql`
  fragment defaultProductFragment on Product {
    _id
    name
    code
    coverImage
    status
    assets(includeAllStatuses: true) {
      ...assetsFragment
    }
    updatedAt
    price
    summary
    tags
    remarks
    fullDescription
    relatedProducts {
      ...relatedProductFragment
    }
    relatedPosts {
      ...relatedPostsFragment
    }
    metadata {
      _id
      key
      value
      unit
      description
    }
    inStock
    inventory {
      _id
      quantityOnHand
    }
    forSale
    hasSubComponents
    computedQuantityOnHand
    requiredComputedQuantityOnHand
    harmonizedTariffCode
    minimumQuantityAllowance
    productType
    operations {
      ...operationsFragment
    }
    totalManufactureCost
    miscellaneousCost
    requiredSerialNumber
    remarksActivity {
      ...productRemarksActivityFragment
    }
    vendors {
      ...productVendorsFragment
    }
    originalPrice
    discountPercentage
    fullBillOfMaterials
    serialNumberType
    baseUom {
      ...uomReferenceFragment
    }
    orderUom {
      ...uomReferenceFragment
    }
    orderConversionFactor
    purchaseUom {
      ...uomReferenceFragment
    }
    purchaseConversionFactor
    workOrderUom {
      ...uomReferenceFragment
    }
    workOrderConversionFactor
    unitOfMeasurePrecision
  }
  ${assetsFragment1}
  ${relatedPostsFragment1}
  ${relatedProductFragment1}
  ${operationsFragment1}
  ${remarksActivityFragment1}
  ${productVendorsFragment1}
  ${uomReferenceFragment}
`

export const buildProductQuery = (field?: BuildGraphqlQueryFragment, subDocuments: string[] = []) => {
  return buildGraphqlQuery('Product', field ?? '_id', subDocuments)
}

export const productListQuery = gql`
  query productList($filter: JSON, $options: JSON) {
    productList(filter: $filter, options: $options) {
      _id
      code
      name
      status
      price
      coverImage
      coverImageThumb
      remarks
      tags
      inventory {
        _id
        quantityOnHand
      }
      requiredComputedQuantityOnHand
      hasSubComponents
      productType
      metadataObject
      inStock
      remarksActivity {
        ...productRemarksActivityFragment
      }
      discountPercentage
      baseUom {
        _id
        code
        symbol
      }
      orderUom {
        _id
        code
        name
        symbol
      }
      unitOfMeasurePrecision
    }
    productListMeta(filter: $filter) {
      count
    }
  }
  ${remarksActivityFragment1}
`

export const productQueries = buildGraphqlQuery('Product', defaultProductFragment)

export const buildCloneProductQuery = (field = '') =>
  gql`
    mutation cloneProduct($input: CloneProductArgs!) {
      cloneProduct(input: $input) {
        _id
        ${field}
      }
    }
  `

// UnitOfMeasure queries
export const unitOfMeasureDefaultFragment = gql`
  fragment unitOfMeasureDefaultFragment on UnitOfMeasure {
    _id
    code
    name
    symbol
    category
    conversionFactor
    status
    updatedAt
  }
`

export const unitOfMeasureQueries = buildGraphqlQuery('UnitOfMeasure', unitOfMeasureDefaultFragment)
